// The calendar's shift status buckets and chip palette. This lives in
// `lib/` (like `entity_palette.ts`) because the v3 chat colors its shift
// reference chips from the same palette, and the v3 feature's import
// boundary must not evaluate the calendar component graph. The calendar
// re-exports everything here, so calendar code keeps importing from
// `calendar_model.ts`.
import { type UnifiedShiftStatus } from '@apps/web/components/dashboard/shift_list_filters';
import { assert } from '@libraries/typescript/utilities';

export type CalendarStatusBucket =
    | 'open'
    | 'scheduled'
    | 'pending_confirmation'
    | 'confirmed'
    | 'in_progress'
    | 'completed'
    | 'clock_issue'
    | 'attention'
    | 'cancelled';

export type CalendarChipBucket = CalendarStatusBucket | 'outreaching';

// Mirrors CalendarChipBucket for runtime (Zod) validation of persisted
// filter state; a stored bucket that is no longer valid fails validation.
export const CALENDAR_CHIP_BUCKET_VALUES = [
    'open',
    'scheduled',
    'pending_confirmation',
    'confirmed',
    'in_progress',
    'completed',
    'clock_issue',
    'attention',
    'cancelled',
    'outreaching',
] as const satisfies ReadonlyArray<CalendarChipBucket>;

export type CalendarChipPalette = {
    readonly label: string;
    // Highlighted buckets (active states) get a strong tint plus a bold
    // outline; quiet buckets (settled states) get a light tint.
    readonly tier: 'highlighted' | 'quiet';
    readonly chipClassName: string;
    readonly outlineClassName?: string;
    readonly dotClassName: string;
    // Text color for the popover's uppercase status eyebrow.
    readonly eyebrowClassName: string;
};

export function resolveCalendarStatusBucket(
    status: UnifiedShiftStatus,
): CalendarStatusBucket {
    switch (status) {
        case 'open':
            return 'open';
        case 'assigned':
            return 'scheduled';
        case 'pending_confirmation':
        case 'confirmation_follow_up':
            return 'pending_confirmation';
        case 'confirmed':
            return 'confirmed';
        case 'in_progress':
            return 'in_progress';
        case 'completed':
            return 'completed';
        case 'clock_in_reminder':
        case 'clock_out_reminder':
            return 'clock_issue';
        case 'declined':
        case 'missing':
        case 'needs_attention':
        case 'needs_review':
        case 'no_response':
        case 'not_scheduled':
        case 'unknown':
            return 'attention';
        case 'cancelled':
            return 'cancelled';
        default:
            return assert.never(status);
    }
}

// Only triage-worthy buckets keep a distinct highlighted color: open (red),
// outreaching (purple), clock_issue/attention (orange). Confirmed gets a
// quiet green tint so schedulers can spot confirmed coverage at a glance.
// The other settled buckets (scheduled/pending_confirmation/in_progress)
// share one quiet neutral treatment — their specific state shows as in-card
// text — and completed/cancelled stay quiet grey. Dark mode inverts each tint.
const NEUTRAL_QUIET_CHIP_TREATMENT: Omit<CalendarChipPalette, 'label'> = {
    tier: 'quiet',
    chipClassName:
        'border-[#9EADB0] bg-[#DBE3E3] text-[#244045] dark:border-[#42595E] dark:bg-[#0D3036] dark:text-[#BDC9C9]',
    dotClassName: 'bg-[#617578] dark:bg-[#9EADB0]',
    eyebrowClassName: 'text-[#617578] dark:text-[#9EADB0]',
};

// Past open shifts cannot be filled anymore, so they read as history in quiet grey.
export const PAST_OPEN_SHIFT_CHIP_PALETTE: CalendarChipPalette = {
    label: 'Open',
    ...NEUTRAL_QUIET_CHIP_TREATMENT,
};

export const CALENDAR_CHIP_PALETTE: Record<
    CalendarChipBucket,
    CalendarChipPalette
> = {
    open: {
        label: 'Open',
        tier: 'highlighted',
        chipClassName:
            'border-[#FF3B57] bg-[#FFA8B2] text-[#420000] dark:border-[#A8002E] dark:bg-[#420000]/70 dark:text-[#FFF0F2]',
        outlineClassName: 'border-2 border-[#E0003D] dark:border-[#FF858F]',
        dotClassName: 'bg-[#E0003D] dark:bg-[#FF858F]',
        eyebrowClassName: 'text-[#E0003D] dark:text-[#FF858F]',
    },
    outreaching: {
        label: 'Outreaching',
        tier: 'highlighted',
        chipClassName:
            'border-[#8C82FF] bg-[#C4C2FF] text-[#1F125E] dark:border-[#5533EE] dark:bg-[#1F125E]/70 dark:text-[#F5F2FF]',
        outlineClassName: 'border-2 border-[#5533EE] dark:border-[#8C82FF]',
        dotClassName: 'bg-[#5533EE] dark:bg-[#8C82FF]',
        eyebrowClassName: 'text-[#5533EE] dark:text-[#8C82FF]',
    },
    scheduled: {
        label: 'Scheduled',
        ...NEUTRAL_QUIET_CHIP_TREATMENT,
    },
    pending_confirmation: {
        label: 'Not yet confirmed',
        ...NEUTRAL_QUIET_CHIP_TREATMENT,
    },
    confirmed: {
        label: 'Confirmed',
        tier: 'quiet',
        chipClassName:
            'border-[#6FCB97] bg-[#C9F2DA] text-[#0B3D24] dark:border-[#1F7A4A] dark:bg-[#0B3D24]/70 dark:text-[#E6FAEE]',
        dotClassName: 'bg-[#0F9A55] dark:bg-[#34D399]',
        eyebrowClassName: 'text-[#0F9A55] dark:text-[#34D399]',
    },
    in_progress: {
        label: 'In progress',
        ...NEUTRAL_QUIET_CHIP_TREATMENT,
    },
    completed: {
        label: 'Completed',
        tier: 'quiet',
        chipClassName:
            'border-[#9EADB0] bg-[#DBE3E3] text-[#244045] dark:border-[#42595E] dark:bg-[#0D3036] dark:text-[#BDC9C9]',
        dotClassName: 'bg-[#617578] dark:bg-[#9EADB0]',
        eyebrowClassName: 'text-[#617578] dark:text-[#9EADB0]',
    },
    clock_issue: {
        label: 'Clock issue',
        tier: 'highlighted',
        chipClassName:
            'border-[#F05238] bg-[#FFAB96] text-[#380F00] dark:border-[#A31A0F] dark:bg-[#380F00]/70 dark:text-[#FFF0ED]',
        outlineClassName: 'border-2 border-[#D13621] dark:border-[#FF785C]',
        dotClassName: 'bg-[#D13621] dark:bg-[#FF785C]',
        eyebrowClassName: 'text-[#D13621] dark:text-[#FF785C]',
    },
    attention: {
        label: 'Needs attention',
        tier: 'highlighted',
        chipClassName:
            'border-[#F05238] bg-[#FFAB96] text-[#380F00] dark:border-[#A31A0F] dark:bg-[#380F00]/70 dark:text-[#FFF0ED]',
        outlineClassName: 'border-2 border-[#D13621] dark:border-[#FF785C]',
        dotClassName: 'bg-[#D13621] dark:bg-[#FF785C]',
        eyebrowClassName: 'text-[#D13621] dark:text-[#FF785C]',
    },
    cancelled: {
        label: 'Cancelled',
        tier: 'quiet',
        chipClassName:
            'border-[#9EADB0] bg-[#BDC9C9] text-[#42595E] dark:border-[#42595E] dark:bg-[#0D3036] dark:text-[#9EADB0]',
        dotClassName: 'bg-[#829494] dark:bg-[#9EADB0]',
        eyebrowClassName: 'text-[#829494] dark:text-[#9EADB0]',
    },
};
